import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { Bell, ChevronLeft, ChevronRight, Menu, Search as SearchIcon, User, X } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, useWindowDimensions, View } from 'react-native';

import { HomeSwitcher } from '@/components/HomeSwitcher';
import { Logo, LogoMark } from '@/components/Logo';
import { ToolsMenu } from '@/components/ToolsMenu';
import { Fonts, Palette } from '@/constants/theme';
import { useDataRevision } from '@/lib/data-revision';
import { webTransition } from '@/lib/hover-style';
import { markRead, useTickerMessages, useUnreadCount } from '@/lib/messages';
import { triggerScrollToTop } from '@/lib/scroll-bus';
import { getActiveProperty, listProperties } from '@/lib/tasks';

/**
 * Persistent top bar: logo on the left, search stub in the middle, avatar
 * on the right. The "search stub" is intentionally a Pressable that looks
 * like an input — tapping it navigates to the dedicated /search screen,
 * which keeps focus + keyboard handling in one place instead of fighting
 * the global header on every screen.
 *
 * Width-based behavior:
 *   - Below NARROW_BREAKPOINT (360): wordmark collapses to mark only.
 *   - Below MOBILE_BREAKPOINT (640): the inline search stub is replaced
 *     by a single round Search icon button styled like the bell/menu.
 *     On a phone the full-width pill bar competes with the bell, menu,
 *     and avatar for thumb real estate; the icon-button version keeps
 *     the trio of right-side actions visually balanced and lets the
 *     logo breathe.
 *   - At or above MOBILE_BREAKPOINT: search stub renders inline. Tablets
 *     and desktops have the room to spare and the longer placeholder
 *     ("Search anything…") helps with affordance.
 *
 * The avatar is always 36px so the right edge stays stable across widths.
 */
const NARROW_BREAKPOINT = 360;
const MOBILE_BREAKPOINT = 640;

export function AppHeader() {
  const router = useRouter();
  const db = useSQLiteContext();
  const { width } = useWindowDimensions();
  const [name, setName] = useState<string | null>(null);
  const [accent, setAccent] = useState<string | null>(null);
  const [showTicker, setShowTicker] = useState(true);
  // Session-only hide. Tapping × on the ticker just collapses the bar
  // for this session — messages stay in the inbox so the user can still
  // scroll their history at /messages. Cleared on reload.
  const [tickerHiddenThisSession, setTickerHiddenThisSession] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // homesCount drives the brand-area switcher swap. >= 2 turns the
  // logo+wordmark into [Mark][HomeName][▾] that opens the HomeSwitcher.
  const [homesCount, setHomesCount] = useState(0);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const revision = useDataRevision();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [p, list] = await Promise.all([
        getActiveProperty(db),
        listProperties(db),
      ]);
      if (cancelled) return;
      setName(p?.name ?? null);
      setAccent(p?.accent_color ?? null);
      // Default ON if column missing (older DB) — the !== 0 trick treats
      // null/undefined as "show," only 0 hides.
      setShowTicker(p?.show_message_ticker !== 0);
      setHomesCount(list.length);
    })();
    return () => { cancelled = true; };
  }, [db, revision]);

  const compact = width < NARROW_BREAKPOINT;
  const isMobile = width < MOBILE_BREAKPOINT;
  const initial = (name ?? '').trim().charAt(0).toUpperCase() || null;
  // Multi-home accounts get the brand-area switcher: the "Homestead"
  // wordmark is replaced with the active home name + chevron, and tapping
  // it opens the same HomeSwitcher modal used elsewhere in the app.
  // Single-home keeps the classic logo-tap-to-go-home behavior.
  // Multi-home accounts get the brand-area switcher. Falls back to a
  // placeholder label when a home was added without a nickname so the
  // switcher still appears (previously this short-circuited to the
  // "Homestead" wordmark when name was null/empty, hiding the
  // switcher entirely for users who never named their new home).
  const multiHome = homesCount >= 2;
  const displayName = (name && name.trim()) || 'Untitled home';
  const avatarBg = accent ?? Palette.forest;
  const unread = useUnreadCount();
  // Ticker only iterates messages NOT flagged as tickerHidden — the inbox
  // still shows them and the unread count still counts them. The ticker
  // is reserved for home-relevant nudges (onboarding, missing system
  // info, expiring warranties, weather), not feature announcements.
  const msgs = useTickerMessages();
  // Walks through eligible messages by index — newest first. Pinning to
  // index 0 when messages change keeps the user from getting stuck on a
  // stale slot after dismissing. Two arrows let them flip backward or
  // forward through the inbox without opening it.
  const [tickerIdx, setTickerIdx] = useState(0);
  useEffect(() => {
    if (tickerIdx >= msgs.length) setTickerIdx(0);
  }, [msgs.length, tickerIdx]);
  const ticker = msgs[tickerIdx] ?? null;
  const canPrev = tickerIdx > 0;
  const canNext = tickerIdx < msgs.length - 1;

  return (
    <View>
      <View style={styles.bar}>
        {multiHome ? (
          // Multi-home: brand area splits into two tap targets so the
          // user keeps "tap mark to go Today" while ALSO getting the
          // always-available switcher on the name + chevron. Single
          // Pressable would have forced us to choose one or the other —
          // we want both.
          <View style={styles.brandSplit}>
            <Pressable
              onPress={() => {
                router.push('/(tabs)' as never);
                triggerScrollToTop();
              }}
              accessibilityRole="button"
              accessibilityLabel="Homestead — home"
              hitSlop={6}
              style={({ pressed, hovered }) => [
                styles.logoBtn,
                webTransition,
                hovered && { opacity: 0.78 },
                pressed && { opacity: 0.85 },
              ]}>
              <LogoMark size={30} />
            </Pressable>
            <Pressable
              onPress={() => setSwitcherOpen(true)}
              accessibilityRole="button"
              accessibilityLabel={`Switch home — currently ${displayName}`}
              hitSlop={6}
              style={({ pressed, hovered }) => [
                styles.switcherTrigger,
                webTransition,
                hovered && { opacity: 0.78 },
                pressed && { opacity: 0.85 },
              ]}>
              {/* iconOnly so the LogoMark inside Logo is suppressed; we
                  render the mark ourselves above so each half owns one
                  visual element. */}
              <Logo size={30} hideSubmark homeName={displayName} iconOnly={false} mark={false} />
            </Pressable>
          </View>
        ) : (
          <Pressable
            onPress={() => {
              // Single-home original behavior:
              //   1. Navigate to home (if user is on a different tab).
              //      The home tab's useFocusEffect will handle scroll
              //      reset for that case.
              //   2. Scroll home to top (if user is already on home).
              //      Focus doesn't change, so we have to dispatch the
              //      scroll signal ourselves via the bus. Calling both
              //      every time is safe — the bus only triggers the
              //      currently-registered scrollers, and "navigate to
              //      where you already are" is a no-op.
              router.push('/(tabs)' as never);
              triggerScrollToTop();
            }}
            accessibilityRole="button"
            accessibilityLabel="Homestead — home"
            hitSlop={6}
            style={({ pressed, hovered }) => [
              styles.logoBtn,
              webTransition,
              hovered && { opacity: 0.78 },
              pressed && { opacity: 0.85 },
            ]}>
            {compact ? <LogoMark size={30} /> : <Logo size={30} hideSubmark />}
          </Pressable>
        )}

        {/*
          On mobile we collapse to a Search icon button styled like the
          bell / menu so the header doesn't feel cramped. A spacer with
          flex:1 keeps the right-side trio anchored to the right edge.
          On larger viewports the inline stub returns — same destination,
          better affordance when there's room.
        */}
        {isMobile ? (
          <>
            <View style={{ flex: 1 }} />
            <Pressable
              onPress={() => router.push('/search' as never)}
              hitSlop={6}
              accessibilityRole="search"
              accessibilityLabel="Search"
              style={({ pressed, hovered }) => [
                styles.iconBtn,
                webTransition,
                hovered && styles.iconBtnHover,
                pressed && { opacity: 0.85 },
              ]}>
              <SearchIcon size={18} color={Palette.forest} strokeWidth={1.9} />
            </Pressable>
          </>
        ) : (
          <Pressable
            onPress={() => router.push('/search' as never)}
            style={({ pressed, hovered }) => [
              styles.searchStub,
              webTransition,
              hovered && styles.searchStubHover,
              pressed && { opacity: 0.85 },
            ]}
            accessibilityRole="search">
            <SearchIcon size={16} color={Palette.inkFaint} strokeWidth={2} />
            <Text style={styles.searchText} numberOfLines={1}>
              {compact ? 'Search…' : 'Search anything…'}
            </Text>
          </Pressable>
        )}

        <Pressable
          onPress={() => router.push('/messages' as never)}
          hitSlop={6}
          accessibilityLabel={
            unread > 0 ? `${unread} unread messages` : 'Open messages'
          }
          style={({ pressed, hovered }) => [
            styles.bell,
            webTransition,
            hovered && styles.iconBtnHover,
            pressed && { opacity: 0.85 },
          ]}>
          <Bell size={18} color={Palette.forest} strokeWidth={1.9} />
          {unread > 0 ? (
            <View style={styles.bellDot}>
              <Text style={styles.bellDotText}>
                {unread > 9 ? '9+' : String(unread)}
              </Text>
            </View>
          ) : null}
        </Pressable>

        {/* Tools menu launcher — slides up a bottom sheet listing every
            secondary surface (Schedule, Inventory, Expenses, etc.) plus
            Account / Home settings rows. Sits between bell and avatar so
            the avatar stays the rightmost account anchor. */}
        <Pressable
          onPress={() => setMenuOpen(true)}
          hitSlop={6}
          accessibilityLabel="Open tools menu"
          style={({ pressed, hovered }) => [
            styles.menuBtn,
            webTransition,
            hovered && styles.iconBtnHover,
            pressed && { opacity: 0.85 },
          ]}>
          <Menu size={18} color={Palette.forest} strokeWidth={1.9} />
        </Pressable>

        <Pressable
          onPress={() => router.push('/account' as never)}
          hitSlop={6}
          style={({ pressed, hovered }) => [
            styles.avatar,
            { backgroundColor: avatarBg },
            webTransition,
            hovered && { opacity: 0.88, transform: [{ scale: 1.04 }] },
            pressed && { opacity: 0.85 },
          ]}
          accessibilityLabel="Open your account">
          {initial ? (
            <Text style={styles.avatarText}>{initial}</Text>
          ) : (
            <User size={18} color={Palette.cream} strokeWidth={1.8} />
          )}
        </Pressable>
      </View>

      <ToolsMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* Brand-area home switcher (multi-home only). Mounted unconditionally
          so the modal can animate its own enter/exit; the underlying
          HomeSwitcher returns null when visible=false. */}
      <HomeSwitcher
        visible={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
      />

      {/* Single-line ticker just below the bar. Shows messages one at a
          time with prev/next arrows so the user can flip through without
          opening the inbox. Close button dismisses the current message
          entirely. Whole thing can be turned off in Settings → View &
          layout. */}
      {showTicker && ticker && !tickerHiddenThisSession ? (
        <View
          style={[
            styles.ticker,
            ticker.tone === 'achievement' && { backgroundColor: '#F0E2BC' },
            ticker.tone === 'warning' && { backgroundColor: 'rgba(176,95,60,0.13)' },
          ]}>
          <Pressable
            onPress={() => setTickerIdx(Math.max(0, tickerIdx - 1))}
            disabled={!canPrev}
            hitSlop={6}
            accessibilityLabel="Previous message"
            style={({ pressed }) => [
              styles.tickerArrow,
              !canPrev && { opacity: 0.3 },
              pressed && canPrev && { opacity: 0.6 },
            ]}>
            <ChevronLeft size={14} color={Palette.inkSoft} strokeWidth={2} />
          </Pressable>
          <Pressable
            onPress={() => {
              // Mark the active ticker entry read so it stops appearing
              // here (useTickerMessages filters by !read). The inbox
              // still keeps the row in its history.
              if (!ticker.read) void markRead(db, ticker.id);
              // Honor a ctaRoute when set (welcome card → /profile-setup)
              // so a single tap is the actual action, not "open inbox
              // and figure out what this is." Falls back to /messages
              // for plain announcements with no destination.
              if (ticker.ctaRoute) {
                router.push(ticker.ctaRoute as never);
              } else {
                router.push('/messages' as never);
              }
            }}
            style={({ pressed }) => [styles.tickerBody, pressed && { opacity: 0.85 }]}>
            <Text style={styles.tickerText} numberOfLines={1}>
              <Text style={styles.tickerTitle}>{ticker.title}</Text>
              {ticker.body ? <Text style={styles.tickerBodyText}> · {ticker.body}</Text> : null}
            </Text>
          </Pressable>
          <Pressable
            onPress={() => setTickerIdx(Math.min(msgs.length - 1, tickerIdx + 1))}
            disabled={!canNext}
            hitSlop={6}
            accessibilityLabel="Next message"
            style={({ pressed }) => [
              styles.tickerArrow,
              !canNext && { opacity: 0.3 },
              pressed && canNext && { opacity: 0.6 },
            ]}>
            <ChevronRight size={14} color={Palette.inkSoft} strokeWidth={2} />
          </Pressable>
          <Pressable
            onPress={() => setTickerHiddenThisSession(true)}
            hitSlop={6}
            accessibilityLabel="Close message ticker for this session"
            style={({ pressed }) => [styles.tickerClose, pressed && { opacity: 0.6 }]}>
            <X size={13} color={Palette.inkSoft} strokeWidth={2.2} />
          </Pressable>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: Palette.sand,
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
  },
  logoBtn: {
    paddingRight: 4,
  },
  // Multi-home: brand area splits into mark (→ Today) + switcher
  // (name+chevron, → modal). Tight gap reads as "one brand area" but
  // keeps two independent tap targets.
  brandSplit: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  switcherTrigger: {
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 8,
  },
  searchStub: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: 38,
    paddingHorizontal: 12,
    backgroundColor: Palette.card,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  searchText: {
    flex: 1,
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.inkFaint,
    letterSpacing: 0.1,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  avatarText: {
    fontFamily: Fonts.serifBold,
    fontSize: 15,
    color: Palette.cream,
    letterSpacing: -0.3,
  },
  bell: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  // Used for the mobile search collapse. Matches the bell exactly so the
  // four right-side controls (Search, Bell, Menu, Avatar) read as one
  // visual cluster at the same rhythm.
  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  // Shared hover state for the round icon buttons (bell, menu, search).
  // A soft warm tint + slight forest border to telegraph interactivity
  // without breaking the calm palette.
  iconBtnHover: {
    backgroundColor: '#F0E9D5',
    borderColor: Palette.forestLight,
  },
  // The search stub gets a parallel-but-subtler treatment so it reads
  // as "I'm an input" hover vs the buttons' "I'm a click" hover.
  searchStubHover: {
    borderColor: Palette.forestLight,
    backgroundColor: '#FBF6EA',
  },
  // Slightly smaller than bell/avatar so the trio reads as
  // primary (bell) → secondary (menu) → identity (avatar) and the
  // header stays balanced even on narrow viewports.
  menuBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  bellDot: {
    position: 'absolute',
    top: -3,
    right: -3,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    paddingHorizontal: 4,
    backgroundColor: Palette.clay,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: Palette.sand,
  },
  bellDotText: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.cream,
    lineHeight: 12,
  },
  ticker: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 6,
    gap: 4,
    backgroundColor: '#E8E0CC',
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
  },
  tickerArrow: {
    width: 24,
    height: 24,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tickerBody: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: 4,
  },
  tickerText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    lineHeight: 17,
  },
  tickerTitle: {
    fontFamily: Fonts.sansBold,
    color: Palette.ink,
  },
  tickerBodyText: {
    color: Palette.inkSoft,
  },
  tickerClose: {
    width: 24,
    height: 24,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.4)',
  },
});
